import {
  TAB_DEFAULT_PRACTICE_BPM,
  validateTabSong,
  type TabImportInput,
  type TabNote,
  type TabSong,
  type TabString,
} from './types';

/** Fast path for untimed, labelled ASCII tab. Ambiguous notation stays with the model. */
export function parseAsciiTab(input: TabImportInput): TabSong | undefined {
  if (!input.text || input.instructions?.trim()) return;
  const text = input.text.replace(/\r/g, '');
  // Also accepts rows separated by spaces after a chat paste loses its newlines.
  const pattern =
    /\b([eBGDAE])\s*\|([-\d/\\hp~| \t]*?)(?=\s+[eBGDAE]\s*\||\n|$|\s*\[)/gi;
  const rows = [...text.matchAll(pattern)];
  if (!rows.length) return;
  const metadata = text.replace(pattern, '');
  // Written rhythm, tuning/capo changes and techniques needing interpretation use AI.
  if (
    /\b(capo|tuning|tuned|drop|open|detune|semitone|quarter|eighth|sixteenth|triplet|dotted|swing)\b|\d\s*\/\s*\d/i.test(
      metadata
    ) ||
    /^\s*(?:[WHQEST][. \t]*){2,}\s*$/m.test(metadata)
  )
    return;
  // Never consume just a readable prefix of a malformed or unsupported string row.
  if (/[eBGDAE]\s*\|/i.test(metadata)) return;
  if (/^\s*(?:[A-G][#b]?(?:m|maj7|m7|7|sus[24])?\s+){2,}/m.test(metadata))
    return;
  const groups: (typeof rows)[] = [];
  if (rows.length === 1 && rows[0][1] !== 'E') groups.push(rows);
  else {
    if (rows.length % 6) return;
    for (let i = 0; i < rows.length; i += 6) {
      const group = rows.slice(i, i + 6);
      if (group.map((r) => r[1].toLowerCase()).join('') !== 'ebgdae') return;
      groups.push(group);
    }
  }
  const markedBpm = metadata.match(
    /\b(\d{2,3})\s*bpm\b|\btempo\s*[:=]\s*(\d{2,3})\b/i
  );
  const bpm =
    input.bpm ??
    (markedBpm
      ? Number(markedBpm[1] ?? markedBpm[2])
      : TAB_DEFAULT_PRACTICE_BPM);
  const song: TabSong = {
    id: 'imported-tab',
    title: 'Imported tab',
    defaultBpm: bpm,
    tuning: { name: 'Standard', notes: ['E', 'A', 'D', 'G', 'B', 'E'] },
    measures: [],
    events: [],
    sections: [],
    source: {
      title: 'Imported tab',
      description: 'Private ASCII tab import.',
      reviewNotes: [
        `Approximate even-eighth practice timing in 4/4 at ${bpm} BPM.`,
        'Standard tuning and no capo assumed. Slides and hammer-ons/pull-offs are simplified to their fret sequence.',
      ],
    },
  };
  let previousEnd = 0;
  for (const group of groups) {
    const heading = [
      ...text
        .slice(previousEnd, group[0].index)
        .matchAll(
          /\[([^\]\n]{1,80})\]|(?:^|\n)\s*((?:intro|verse|pre[- ]?chorus|chorus|bridge|outro|solo|interlude|refrain|instrumental)(?:[ \t]+\d+)?)[ \t]*:?\s*(?=\n|$)/gi
        ),
    ].pop();
    const sectionName = heading?.[1] ?? heading?.[2];
    const columns = new Map<number, TabNote[]>();
    for (const [index, row] of group.entries()) {
      const string = (
        group.length === 6
          ? index + 1
          : ({ e: 1, B: 2, G: 3, D: 4, A: 5 } as Record<string, number>)[row[1]]
      ) as TabString;
      if (!string) return;
      for (const token of row[2].matchAll(/\d+/g)) {
        const fret = Number(token[0]);
        if (fret > 24) return;
        const col = token.index!;
        columns.set(col, [...(columns.get(col) ?? []), { string, fret }]);
      }
    }
    const attacks = [...columns.entries()]
      .sort(([a], [b]) => a - b)
      .map(([, notes]) => notes);
    if (!attacks.length) return; // A blank system may encode rests we cannot infer.
    const startMeasure = song.measures.length + 1;
    for (let i = 0; i < attacks.length; i += 8) {
      const number = song.measures.length + 1;
      const startBeat = (number - 1) * 4;
      song.measures.push({
        number,
        startBeat,
        durationBeats: 4,
        timeSignature: { numerator: 4, denominator: 4 },
      });
      const batch = attacks.slice(i, i + 8);
      batch.forEach((notes, offset) =>
        song.events.push({
          id: `e${song.events.length + 1}`,
          measure: number,
          startBeat: startBeat + offset * 0.5,
          durationBeats: 0.5,
          type: 'note',
          notes,
          attack: 'pick',
        })
      );
      if (batch.length < 8)
        song.events.push({
          id: `e${song.events.length + 1}`,
          measure: number,
          startBeat: startBeat + batch.length * 0.5,
          durationBeats: 4 - batch.length * 0.5,
          type: 'rest',
          notes: [],
        });
    }
    const last = song.sections[song.sections.length - 1];
    if (!sectionName && last) last.endMeasure = song.measures.length;
    else
      song.sections.push({
        id: `s${song.sections.length + 1}`,
        name: sectionName ?? 'Full song',
        startMeasure,
        endMeasure: song.measures.length,
      });
    const final = group[group.length - 1];
    previousEnd = final.index! + final[0].length;
  }
  return validateTabSong(song).length ? undefined : song;
}
